import React, { useCallback, useEffect, useState } from 'react';
import EmptyState from '../common/EmptyState';
import Skeleton from '../common/Skeleton';
import InlineBanner from '../common/InlineBanner';
import MatchCard from '../search/MatchCard';
import { findSimilar } from '../../services/ReverseSearchService.mjs';
import { describeIndexState } from '../../utils/reverseSearchFormat.js';
import '../../css/SimilarAssets.css';

/**
 * "Similar assets" — find files by CONTENT rather than by name or tag.
 *
 * The cheapest entry point into reverse search, and deliberately the first one
 * shipped (REVERSE_SEARCH_PLAN.md Decision G): both operands are already
 * indexed, so this is a handful of Firestore queries with no upload and no
 * decode — cheap enough to hang off a modal tab.
 *
 * ── Two rules the rendering follows ─────────────────────────────────────────
 *
 * 1. **Never show a similarity number.** A percentage invites a user to compare
 *    two numbers they have no way to calibrate, and it turns the panel into a
 *    slot machine. Each result gets a BAND ("Near-identical", "Similar") and
 *    the EVIDENCE that earned it ("Region at 34%, 12%") — a claim they can
 *    check by looking.
 *
 * 2. **A guess must not look like a measurement.** Enforced by `MatchCard`,
 *    which is shared with the drop-a-file results view so the two cannot drift
 *    apart about what a match looks like.
 */

/** Results requested. Enough to fill the rail without turning a modal tab into
 *  a second file browser — anything past a couple of screens means the user
 *  wants the real search, not this panel. */
const RESULT_LIMIT = 24;

const SimilarAssetsSection = ({ file, onOpenFile }) => {
    const [state, setState] = useState({
        status: 'idle', results: [], indexState: null, emptySpaces: [], indexing: false,
    });

    const load = useCallback(async (signal) => {
        setState((prev) => ({ ...prev, status: 'loading' }));
        try {
            const data = await findSimilar(file.id, {
                limit: RESULT_LIMIT,
                signal,
                // A failure here means one tab of a modal is empty. That is not
                // worth a global toast interrupting whatever the user was doing;
                // the inline banner below says it in place.
                skipGlobalErrorToast: true,
            });
            setState({
                status: 'ready',
                results: Array.isArray(data?.results) ? data.results : [],
                indexState: data?.indexState ?? null,
                emptySpaces: Array.isArray(data?.emptySpaces) ? data.emptySpaces : [],
                indexing: data?.indexing === true,
            });
        } catch (err) {
            if (signal?.aborted) return;
            setState({
                status: 'error',
                results: [],
                indexState: null,
                emptySpaces: [],
                indexing: false,
                message: err?.response?.data?.error || 'Could not search for similar assets.',
            });
        }
    }, [file.id]);

    // Runs on mount rather than behind a "Search" button: the tab click IS the
    // intent, and making someone click twice to see the thing they opened the
    // tab for is a worse trade than one request they might not have needed.
    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    if (state.status === 'loading' || state.status === 'idle') {
        return (
            <div className="sa-grid" aria-busy="true">
                {Array.from({ length: 6 }, (_, i) => (
                    <Skeleton key={i} className="sa-skeleton" />
                ))}
            </div>
        );
    }

    if (state.status === 'error') {
        return (
            <div className="sa-error">
                <InlineBanner variant="danger">{state.message}</InlineBanner>
                <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={() => load()}>
                    Try again
                </button>
            </div>
        );
    }

    const indexNote = describeIndexState(state.indexState);

    if (state.results.length === 0) {
        return (
            <>
                {indexNote && <InlineBanner variant="info">{indexNote}</InlineBanner>}
                {/* Three distinct situations that would otherwise render as the
                    same empty grid, and only one of them is the user's problem:
                    this file is still being read; nothing in the workspace has
                    been read yet; nothing matches. The first is the one where
                    "check back" is the right advice — and the reason it exists
                    at all is that decoding a long recording takes long enough
                    that doing it inside this request would just hang. */}
                <EmptyState
                    title={state.indexing ? 'Reading this file…' : 'No similar assets'}
                    description={
                        state.indexing
                            ? 'This asset is being analysed for content search. It only happens once — reopen this tab in a moment.'
                            : state.emptySpaces.length > 0
                                ? 'Nothing else in this workspace has been indexed for content search yet. New uploads are indexed automatically; existing files need a one-off backfill.'
                                : 'Nothing else in this workspace looks like this file.'
                    }
                    action={state.indexing ? (
                        <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={() => load()}>
                            Check now
                        </button>
                    ) : undefined}
                />
            </>
        );
    }

    return (
        <>
            {indexNote && <InlineBanner variant="info">{indexNote}</InlineBanner>}
            <div className="sa-grid">
                {state.results.map((hit) => (
                    <MatchCard key={hit.fileId} hit={hit} onOpen={onOpenFile} />
                ))}
            </div>
        </>
    );
};

export default SimilarAssetsSection;
